import React, { useState } from "react";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import {
  Box,
  Button,
  Stack,
  Popover,
  Paper,
  Typography,
  styled,
  IconButton,
  ButtonGroup,
  Tooltip,
  ThemeProvider,
  createTheme,
} from "@mui/material";
import {
  CalendarToday,
  ChevronLeft,
  ChevronRight,
  Search,
  Settings,
  MoreVert,
} from "@mui/icons-material";
import { LocalizationProvider, StaticDatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import { COLORS } from "colors/color";
import "./FCalendar.css";
import EventContent from "./EventContent";
import { useTranslation } from 'react-i18next';
import colors from "colors/color";

// Google Calendar theme
const googleCalendarTheme = createTheme({
  palette: {
    primary: {
      main: '#1a73e8',
      light: '#e8f0fe',
      dark: '#1557b0',
    },
    secondary: {
      main: '#34a853',
      light: '#e6f4ea',
    },
    background: {
      default: '#ffffff',
      paper: '#ffffff',
    },
    grey: {
      50: '#f8f9fa',
      100: '#f1f3f4',
      200: '#e8eaed',
      300: '#dadce0',
      400: '#bdc1c6',
      500: '#9aa0a6',
      600: '#80868b',
      700: '#5f6368',
      800: '#3c4043',
      900: '#202124',
    },
    text: {
      primary: '#3c4043',
      secondary: '#5f6368',
    },
  },
  typography: {
    fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
    h4: {
      fontWeight: 400,
      fontSize: '1.5rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.125rem',
    },
    body1: {
      fontSize: '0.875rem',
    },
    body2: {
      fontSize: '0.75rem',
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 24,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

const HeaderCell = styled(Box)(({ theme, isCurrentDate }) => ({
  textAlign: "center",
  padding: "12px 8px",
  borderBottom: "1px solid #e8eaed",
  backgroundColor: "#ffffff",
  position: 'relative',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '#f8f9fa',
  },
}));

const CustomToolbar = ({ calendarRef }) => {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const [currentDate, setCurrentDate] = useState(new Date());
  const [active, setActive] = useState("timeGridWeek");

  const handleDatePickerClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleDatePickerClose = () => {
    setAnchorEl(null);
  };

  const handleDateChange = (newDate) => {
    if (newDate && calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.gotoDate(newDate.toDate());
      setSelectedDate(newDate);
      setCurrentDate(newDate.toDate());
      handleDatePickerClose();
    }
  };

  const handleViewChange = (view) => {
    setActive(view);
    if (calendarRef.current) {
      calendarRef.current.getApi().changeView(view);
    }
  };

  const navigateDate = (direction) => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      if (direction === 1) {
        calendar.next();
      } else {
        calendar.prev();
      }
      setCurrentDate(calendar.getDate());
    }
  };

  const goToToday = () => {
    if (calendarRef.current) {
      const calendar = calendarRef.current.getApi();
      calendar.today();
      setCurrentDate(new Date());
      setSelectedDate(dayjs());
    }
  };

  return (
    <ThemeProvider theme={googleCalendarTheme}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          pb: 2,
          mb: 2,
          borderBottom: '1px solid',
          borderColor: 'grey.200',
        }}
      >
        {/* Left side - Navigation and Date */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigateDate(-1)}
            size="small"
            sx={{
              '&:hover': { backgroundColor: 'grey.100' },
              color: 'text.primary'
            }}
          >
            <ChevronLeft fontSize="small" />
          </IconButton>
          <IconButton
            onClick={() => navigateDate(1)}
            size="small"
            sx={{
              '&:hover': { backgroundColor: 'grey.100' },
              color: 'text.primary'
            }}
          >
            <ChevronRight fontSize="small" />
          </IconButton>
          <Button
            variant="outlined"
            size="small"
            onClick={goToToday}
            sx={{
              ml: 1,
              borderColor: 'grey.300',
              color: 'text.primary',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
              textTransform: 'none',
              fontWeight: 500,
              '&:hover': {
                borderColor: 'grey.400',
                backgroundColor: 'grey.50'
              }
            }}
          >
            {t('calendar.toolbar.today') || 'Today'}
          </Button>
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              fontWeight: 400,
              color: 'text.primary',
              fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
              fontSize: '1.375rem'
            }}
          >
            {(() => {
              const monthNames = [
                'january', 'february', 'march', 'april', 'may', 'june',
                'july', 'august', 'september', 'october', 'november', 'december'
              ];
              const monthIndex = currentDate.getMonth();
              const monthName = t(`calendar.months.${monthNames[monthIndex]}`);
              const year = currentDate.getFullYear();
              return `${monthName} ${year}`;
            })()}
          </Typography>
        </Box>

        {/* Right side - View switcher and actions */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ButtonGroup size="small" variant="outlined">
            <Button
              variant={active === 'dayGridMonth' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('dayGridMonth')}
              sx={{
                minWidth: 'auto',
                px: 2,
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {t('calendar.toolbar.month') || 'Month'}
            </Button>
            <Button
              variant={active === 'timeGridWeek' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('timeGridWeek')}
              sx={{
                minWidth: 'auto',
                px: 2,
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {t('calendar.toolbar.week') || 'Week'}
            </Button>
            <Button
              variant={active === 'timeGridDay' ? 'contained' : 'outlined'}
              onClick={() => handleViewChange('timeGridDay')}
              sx={{
                minWidth: 'auto',
                px: 2,
                fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                textTransform: 'none',
                fontWeight: 500,
              }}
            >
              {t('calendar.toolbar.day') || 'Day'}
            </Button>
          </ButtonGroup>

          <Tooltip title="Date picker">
            <IconButton
              onClick={handleDatePickerClick}
              size="small"
              sx={{
                ml: 1,
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'grey.100' }
              }}
            >
              <CalendarToday fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* <Tooltip title="Search">
            <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'grey.100' } }}>
              <Search fontSize="small" />
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings">
            <IconButton size="small" sx={{ color: 'text.secondary', '&:hover': { backgroundColor: 'grey.100' } }}>
              <Settings fontSize="small" />
            </IconButton>
          </Tooltip> */}
        </Box>
      </Box>
      {/* Enhanced Date Picker Popover */}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleDatePickerClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        sx={{
          "& .MuiPopover-paper": {
            marginTop: "8px",
            boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
            borderRadius: "12px",
            border: '1px solid',
            borderColor: 'grey.200',
          },
        }}
      >
        <Paper sx={{ p: 2 }}>
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <StaticDatePicker
              displayStaticWrapperAs="desktop"
              value={selectedDate}
              onChange={handleDateChange}
              renderInput={() => null}
              sx={{
                width: 320,
                '& .MuiPickersDay-root': {
                  fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                },
                '& .MuiPickersDay-today': {
                  backgroundColor: 'primary.light',
                  color: 'primary.main',
                },
                '& .Mui-selected': {
                  backgroundColor: 'primary.main',
                  color: 'white',
                },
              }}
            />
          </LocalizationProvider>
        </Paper>
      </Popover>
    </ThemeProvider>
  );
};

const CalendarComponent = ({
  handleEventClick,
  renderEventContent,
  weekendsVisible,
  currentEvents,
}) => {
  const calendarRef = React.useRef(null);
  const containerRef = React.useRef(null);
  const { t } = useTranslation();

  // Add debounced window resize handler
  React.useEffect(() => {
    let timeoutId;
    
    const handleResize = () => {
      clearTimeout(timeoutId);
      timeoutId = setTimeout(() => {
        if (calendarRef.current) {
          const calendar = calendarRef.current.getApi();
          calendar.updateSize();
        }
      }, 150); // Debounce resize events
    };

    window.addEventListener('resize', handleResize);
    
    // Also add a ResizeObserver for container changes
    const resizeObserver = new ResizeObserver(() => {
      if (calendarRef.current) {
        const calendar = calendarRef.current.getApi();
        calendar.updateSize();
      }
    });

    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }
    
    // Cleanup
    return () => {
      window.removeEventListener('resize', handleResize);
      clearTimeout(timeoutId);
      resizeObserver.disconnect();
    };
  }, []);

  return (
    <ThemeProvider theme={googleCalendarTheme}>
      <Box
        ref={containerRef}
        style={{ flexGrow: 1, height: "100%", width: "100%" }}
        sx={{
          backgroundColor: "background.paper",
          padding: 3,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.12), 0 1px 2px rgba(0,0,0,0.24)",
          border: '1px solid',
          borderColor: 'grey.200',
          display: "flex",
          flexDirection: "column",
          justifyContent: "flex-start",
          minWidth: 0, // Allow shrinking
          overflow: "hidden", // Prevent overflow
        }}
      >
        <CustomToolbar calendarRef={calendarRef} />
        <FullCalendar
          ref={calendarRef}
          plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
          initialView="timeGridWeek"
          headerToolbar={false}
          editable={false}
          selectable={false}
          selectMirror={true}
          dayMaxEvents={true}
          weekends={weekendsVisible}
          eventContent={(eventInfo) => <EventContent event={eventInfo.event} />}
          eventBackgroundColor="transparent"
          eventBorder="none"
          eventClick={handleEventClick}
          events={currentEvents}
          height="100%"
          aspectRatio={2}
          nowIndicator={true}
          allDaySlot={false}
          scrollTime={new Date().getHours() + ":00:00"}
          expandRows={true}
          stickyHeaderDates={true}
          handleWindowResize={true}
          slotMinTime="06:00:00"
          slotMaxTime="22:00:00"
          slotDuration="01:00:00"
          slotLabelInterval="01:00:00"
          width="100%"
          dayHeaderContent={(args) => {
            const isCurrentDate =
              args.date.toDateString() === new Date().toDateString();
            
            // Get day name using translations
            const dayNames = [
              'sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'
            ];
            const dayIndex = args.date.getDay();
            const dayName = t(`calendar.days.${dayNames[dayIndex]}`);
            
            const dayNumber = args.date.getDate();

            return (
              <HeaderCell isCurrentDate={isCurrentDate}>
                <Box sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  py: 1,
                }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#5f6368',
                      fontSize: '0.6875rem',
                      fontWeight: 500,
                      letterSpacing: '0.5px',
                      textTransform: 'uppercase',
                      fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                      mb: 0.5,
                    }}
                  >
                    {dayName}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      color: isCurrentDate ? '#1a73e8' : '#3c4043',
                      fontWeight: isCurrentDate ? 600 : 400,
                      fontSize: '0.875rem',
                      fontFamily: '"Google Sans", "Roboto", "Arial", sans-serif',
                      width: isCurrentDate ? '24px' : 'auto',
                      height: isCurrentDate ? '24px' : 'auto',
                      borderRadius: isCurrentDate ? '50%' : '0',
                      backgroundColor: isCurrentDate ? '#1a73e8' : 'transparent',
                      color: isCurrentDate ? '#ffffff' : '#3c4043',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    {dayNumber}
                  </Typography>
                </Box>
              </HeaderCell>
            );
          }}
          className="google-calendar"
        />
      </Box>
    </ThemeProvider>
  );
};

export default CalendarComponent;